import CountryModel from 'coreClasses/countries/CountryModel';
import LanguageModel from 'coreClasses/languages/LanguageModel';
import {DATE_FORMAT_FOR_CONTINENTS} from 'brandConstants';

/**
 * Returns LocaleModel
 */
class LocaleModel {
	private country: CountryModel;
	private language: LanguageModel;

	/**
	 * @constructor
	 * @param {CountryModel} country - country model
	 * @param {LanguageModel} language - language model
	 */
	constructor(country: CountryModel, language: LanguageModel) {
		this.country = country;
		this.language = language;
	}

	/**
	 * Returns locale code
	 * @returns {string|null}
	 */
	getCode(): string|null {
		if (this.isValid()) {
			return `${this.language.getLanguageCode()}-${this.country.getCountryCode().toLowerCase()}`;
		}

		return null;
	}

	/**
	 * Returns locale code in the hreflang format
	 * @returns {string|null}
	 */
	getHreflangCode(): string|null {
		if (this.isValid()) {
			return `${this.language.getLanguageCode()}-${this.country.getCountryCode().toLowerCase()}`;
		}

		return null;
	}

	/**
	 * Returns locale iso code
	 * @param {string} separator
	 * @returns {string|null}
	 */
	getISOCode(separator: string = '-'): string|null {
		if (this.isValid()) {
			return `${this.language.getLanguageCode()}${separator}${this.country.getCountryCode()}`;
		}

		return null;
	}

	/**
	 * Returns locale country
	 * @returns {string}
	 */
	getCountryCode(): string {
		return this.country?.getCountryCode();
	}

	/**
	 * Returns currency code
	 * @returns {string}
	 */
	getCurrencyCode(): string {
		return this.country?.getCurrencyCode();
	}

	/**
	 *
	 * Returns currency symbol
	 * @returns {string}
	 */
	getCurrencySymbol(): string {
		let currencySymbol = '';
		let currency = this.getCurrencyCode();

		if (currency) {
			const formattedData = new Intl.NumberFormat(this.getLanguageCode().toLowerCase(), {
				style: 'currency',
				currency: currency.toUpperCase(),
			}).format(0);

			currencySymbol = formattedData.replace(/\d+([,.]\d+)?\s*/g, '');
		}

		return currencySymbol;
	}

	/**
	 * Returns locale language
	 * @returns {string}
	 */
	getLanguageCode(): string {
		return this.language?.getLanguageCode();
	}

	/**
	 * Returns current site ID
	 * @returns {string}
	 */
	getCurrentSiteId(): string {
		return this.country?.getSiteId();
	}

	/**
	 * Checks if the locale model valid
	 * @returns {boolean}
	 */
	isValid(): boolean {
		return !!this.country && !!this.language;
	}

	/**
	 * Returns locale date format string
	 * @returns {string|null}
	 */
	getDateFormatString(): string|null {
		let format: string|null = null;
		const continents = this.country.getContinents();

		if (this.isValid()) {
			if (continents.length === 1 && DATE_FORMAT_FOR_CONTINENTS && DATE_FORMAT_FOR_CONTINENTS[continents[0]]) {
				format = DATE_FORMAT_FOR_CONTINENTS[continents[0]];
			} else {
				const dateTimeFormat = new Intl.DateTimeFormat(this.getCode());
				const parts = dateTimeFormat.formatToParts(new Date());
				format = parts.map((p) => {
					if (['day', 'month', 'year'].includes(p.type)) {
						return p.type;
					} else {
						return p.value;
					}
				}).join('');
			}
		}

		return format;
	}
}

export default LocaleModel;
